/* eslint-disable react/prop-types */
import {
  Typography,
  useTheme,
  Box,
  Modal,
  Button,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import { colorTheme } from 'theme';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import {
  viewAllPlanChangeRequests,
  updatePlanChangeRequestStatus,
} from 'service/';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { type RootState } from 'store';
import { pagesConstantList } from 'routes/Constant';
import { strings } from 'i18n/Strings';
import {
  commonConstantTexts,
  planChangeStatusTypeOption,
} from 'constants/constantText';

import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { checkSortingValues, dateFormat } from 'utils/GeneralUtil';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';

interface PlanRequests {
  requests: Request[];
}

interface Request {
  requestId: string;
  requestedRetirementPlanId: string;
  requestedPensionPlanId: string;
  status: string;
  user: User;
  requestedRetirementPlan: { planName: string };
  currentRetirementPlan: { planName: string };
  requestedPensionPlan: { name: string };
  currentPensionPlan: { name: string };
  approvedDate: number | string;
  rejected: number | string;
}

interface User {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  callingCode: string;
  phone: string;
}

const PlanChangeRequests = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [planData, setPlanData] = useState<Request[]>([]);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [approve, setApprove] = useState(false);
  const [user, setUser] = useState<Request>();
  const { roles } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector(
    (state) => state?.pagination?.planChangeRequests,
  );
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState<unknown>({
    startDate: null,
    endDate: null,
    status: '',
  });

  const formatPhoneNumber = (
    callingCode?: string,
    phone?: string,
  ): string => {
    if (!phone) return '-';
    const normalizedCallingCode = callingCode
      ? `+${callingCode.replace(/^\++/, '')}`
      : '';
    return normalizedCallingCode
      ? `${normalizedCallingCode} ${phone}`
      : phone;
  };

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
  };

  const getAllPlanRequests = async (): Promise<void> => {
    try {
      const planRequests = (await viewAllPlanChangeRequests(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        pageInfo?.sortBy,
        pageInfo?.sortOrder,
        searchValue,
        filters,
      )) as PlanRequests;
      setPlanData(planRequests?.requests?.rows);
      setPageCount(planRequests?.requests?.count);
    } catch (error) {
      console.log(error);
    }
  };

  const handleApproval = async (row: Request): Promise<void> => {
    try {
      const requestInfo = {
        requestId: row?.requestId,
        userId: row?.user?.userId,
        requestedRetirementPlanId: row?.requestedRetirementPlanId,
        requestedPensionPlanId: row?.requestedPensionPlanId,
        status: commonConstantTexts.approved,
      };
      setLoader(true);
      updatePlanChangeRequestStatus(requestInfo)
        .then((res) => {
          dispatch(
            setSnackBarSuccess({
              snackBarMessage: strings.displayText.planApprovedSuccessMessage,
            }),
          );
          void getAllPlanRequests();
        })
        .catch((err) => {
          dispatch(
            setSnackBarFailed({
              snackBarMessage: err?.response?.data?.message,
            }),
          );
        });
      await getAllPlanRequests();
      setOpenConfirmationDialog(false);
      setLoader(false);
    } catch (error) {
      console.log(error);
      setLoader(false);
    }
  };

  const handleRejection = async (row: Request): Promise<void> => {
    try {
      const requestInfo = {
        requestId: row?.requestId,
        userId: row?.user?.userId,
        requestedRetirementPlanId: row?.requestedRetirementPlanId,
        requestedPensionPlanId: row?.requestedPensionPlanId,
        status: commonConstantTexts.rejected,
      };
      setLoader(true);
      updatePlanChangeRequestStatus(requestInfo)
        .then(() => {
          dispatch(
            setSnackBarSuccess({
              snackBarMessage: strings.displayText.planRejectedSuccessMessage,
            }),
          );
          void getAllPlanRequests();
        })
        .catch(() => {
          dispatch(
            setSnackBarFailed({
              snackBarMessage: strings.displayText.errorMessage,
            }),
          );
        });
      await getAllPlanRequests();
      setOpenConfirmationDialog(false);
      setLoader(false);
    } catch (error) {
      console.log(error);
      setLoader(false);
    }
  };

  const ConfirmationDialog = (): JSX.Element => {
    return (
      <Modal
        open={openConfirmationDialog}
        onClose={() => {
          setOpenConfirmationDialog(false);
        }}
      >
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',

            bgcolor: 'background.paper',
            boxShadow: 24,
            py: 5,
            px: 5,
            borderRadius: 5,
          }}
        >
          <Typography variant="h5" component="h2" sx={{ textAlign: 'center' }}>
            {user &&
              (approve
                ? `Confirm approval of plan for ${user?.user.email}?`
                : `Confirm rejection of plan for ${user?.user.email}?`)}
          </Typography>
          <Box
            sx={{
              pt: 3,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                setOpenConfirmationDialog(false);
              }}
              sx={{
                mt: 3,
                mb: 2,
                bgcolor: 'white',
                borderRadius: 2,
                py: 1.5,
                '&:hover': {
                  bgcolor: colors.gray[800],
                },
              }}
            >
              <Typography
                component="h1"
                variant="h5"
                style={{ fontSize: '12px', color: colors.primaryBg[700] }}
              >
                {strings.displayText.cancel}
              </Typography>
            </Button>
            <Button
              onClick={
                approve
                  ? async () => {
                      await handleApproval(user as Request);
                    }
                  : async () => {
                      await handleRejection(user as Request);
                    }
              }
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                bgcolor: colors.greenAccent[500],
                borderRadius: 2,
                py: 1.5,
              }}
              disabled={loader}
              endIcon={loader ? <CircularProgress size={15} /> : null}
            >
              {approve ? (
                <Typography
                  component="h1"
                  variant="h5"
                  style={{ fontSize: '12px' }}
                >
                  {strings.displayText.approve}
                </Typography>
              ) : (
                <Typography
                  component="h1"
                  variant="h5"
                  style={{ fontSize: '12px' }}
                >
                  {strings.displayText.reject}
                </Typography>
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  };

  useEffect(() => {
    void getAllPlanRequests();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    searchValue,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const handleFilterChange = (filterType: string, value: string): void => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleApplyFilter = () => {
    dispatch(
      setCurrentPage({
        key: commonConstantTexts.planChangeRequestsPage,
        value: 0,
      }),
    );

    void getAllPlanRequests();
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'requestId',
      width: 120,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.planChangeRequestId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.requestId}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.requestedDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.createdAt)}
        </Typography>
      ),
    },
    {
      field: 'user.firstName',
      width: 180,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.nameColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.user.firstName} {row.user.lastName}
        </Typography>
      ),
    },
    {
      field: 'user.email',
      width: 260,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.emailAddress}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.user.email}
        </Typography>
      ),
    },
    {
      field: 'user.callingCode/user.phone',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.phoneNumber}
        </Typography>
      ),
      width: 150,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {formatPhoneNumber(row.user.callingCode, row.user.phone)}
        </Typography>
      ),
    },
    {
      field: 'currentPensionPlan.name',
      width: 200,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.existingPensionPlan}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.currentPensionPlan.name}
        </Typography>
      ),
    },
    {
      field: 'currentRetirementPlan.planName',
      width: 200,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.existingRetirementOption}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.currentRetirementPlan.planName}
        </Typography>
      ),
    },
    {
      field: 'requestedPensionPlan.name',
      width: 200,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.newPensionPlan}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.requestedPensionPlan.name}
        </Typography>
      ),
    },
    {
      field: 'requestedRetirementPlan.planName',
      width: 200,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.newRetirementOption}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.requestedRetirementPlan.planName}
        </Typography>
      ),
    },

    {
      field: 'status',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.statusColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.status}
        </Typography>
      ),
    },
    {
      field: 'approvedDate',
      width: 160,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.planChangeRequestsApprovedDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.status === 'Approved' && row.approvedDate
            ? dateFormat(row.approvedDate)
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'rejectDate',
      width: 160,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.planChangeRequestsrejectDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.status === 'Rejected' && row.rejectedDate
            ? dateFormat(row.rejectedDate)
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'orderPlacedDate',
      width: 160,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.planChangeRequestsOrderPlacedDate}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const OrderPlacedDate =
          row.userWiseSalesUnits?.[0]?.orderInfo?.orderDate;
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.status === 'Approved' && OrderPlacedDate
              ? dateFormat(OrderPlacedDate)
              : '-'}
          </Typography>
        );
      },
    },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}></Box>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={5} />
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.startDate}
            type="date"
            value={filters.startDate}
            onChange={(e) => {
              handleFilterChange('startDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.endDate}
            type="date"
            value={filters.endDate}
            onChange={(e) => {
              handleFilterChange('endDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 200, marginTop: 2, marginRight: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Status</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Status"
              value={filters.status}
              onChange={(e) => {
                handleFilterChange('status', e.target.value);
              }}
            >
              {planChangeStatusTypeOption.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            onClick={handleApplyFilter}
          >
            {strings.displayText.filter}
          </LoadingButton>
        </Grid>
      </Grid>

      <CustomDataGrid
        hideActionsCondition={(row) => row.status !== 'Pending'}
        columns={columns}
        rows={planData}
        getRowId={(row: Request) => `${String(row.requestId)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0
            ? commonConstantTexts.evenText
            : commonConstantTexts.oddText
        }
        actionBtnText1={
          roles?.includes(pagesConstantList.ceo)
            ? strings.displayText.approve
            : ''
        }
        ActionBtnIcon1={() => <DoneIcon color="success" />}
        onActionBtnClick1={(row) => {
          setUser(row as unknown as Request);
          setOpenConfirmationDialog(true);
          setApprove(true);
        }}
        actionBtnText2={
          roles?.includes(pagesConstantList.ceo)
            ? strings.displayText.decline
            : ''
        }
        onActionBtnClick2={(row) => {
          setUser(row as unknown as Request);
          setOpenConfirmationDialog(true);
          setApprove(false);
        }}
        ActionBtnIcon2={() => <CloseIcon color="error" />}
        currentPage={commonConstantTexts.planChangeRequestsPage}
        pageCount={pageCount}
        searchEnabled={true}
        handleSearch={handleSearch}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          dispatch(
            setSortBy({
              key: commonConstantTexts.planChangeRequestsPage,
              value: mode[0].field,
            }),
          );
          dispatch(
            setSortOrder({
              key: commonConstantTexts.planChangeRequestsPage,
              value: mode[0].sort,
            }),
          );
          dispatch(
            setCurrentPage({
              key: commonConstantTexts.planChangeRequestsPage,
              value: 0,
            }),
          );
        }}
      />
      <ConfirmationDialog />
    </>
  );
};

export default PlanChangeRequests;
